/**
 * Parâmetros com vigência (CLAUDE.md, regras 7 e 8). Nada se edita nem se apaga:
 * mudança = nova linha com nova data de vigência. Só o admin cadastra (RLS).
 */
import {
  Create,
  CreateButton,
  Datagrid,
  DateInput,
  List,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  required,
  usePermissions,
} from "react-admin";
import Alert from "@mui/material/Alert";
import { CampoData, CampoPercentual, EntradaPercentual } from "../componentes";
import type { Permissoes } from "../lib/provedores";
import { GruposESetores } from "./fornecedores";
import { TiposDocumentoParametro } from "./habilitacao";
import { PerfisPreco } from "./perfis-preco";
import { ModeloPropostaParametro } from "./modelo-proposta";
import { OneDriveParametro } from "./onedrive";
import { RegrasAlertaParametro } from "./alertas";

const avisoHistorico = (
  <Alert severity="info" sx={{ mb: 2 }}>
    Valores com vigência: para mudar, cadastre uma nova linha com a data a partir da qual vale. O histórico não é
    alterado.
  </Alert>
);

function AcoesSoAdmin() {
  const { permissions } = usePermissions<Permissoes | null>();
  return <TopToolbar>{permissions?.papel === "admin" && <CreateButton label="Nova vigência" />}</TopToolbar>;
}

/* ----------------------------- Padrões de preço ----------------------------- */

export function ParametroPrecoLista() {
  return (
    <List
      title="Padrões de preço"
      sort={{ field: "vigente_desde", order: "DESC" }}
      actions={<AcoesSoAdmin />}
      aside={undefined}
      empty={false}
    >
      {avisoHistorico}
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <CampoData source="vigente_desde" />
        <CampoPercentual source="margem" />
        <CampoPercentual source="seguro" />
        <CampoPercentual source="custo_fixo" />
        <CampoPercentual source="simples" />
        <CampoPercentual source="frete_cif_pct" />
        <TextField source="origem" />
      </Datagrid>
    </List>
  );
}

function validarSoma(valores: Record<string, unknown>) {
  const campos = ["margem", "seguro", "custo_fixo", "simples", "frete_cif_pct"];
  const soma = campos.reduce((total, campo) => total + (Number(valores[campo]) || 0), 0);
  return soma >= 1 ? { margem: "A soma dos percentuais do divisor precisa ser menor que 100%." } : {};
}

export function ParametroPrecoCriar() {
  return (
    <Create title="Nova vigência de padrões de preço" redirect="list">
      <SimpleForm validate={validarSoma}>
        {avisoHistorico}
        <DateInput source="vigente_desde" validate={required()} />
        <EntradaPercentual source="margem" validate={required()} />
        <EntradaPercentual source="seguro" validate={required()} />
        <EntradaPercentual source="custo_fixo" validate={required()} />
        <EntradaPercentual source="simples" validate={required()} />
        <EntradaPercentual source="frete_cif_pct" validate={required()} />
        <TextInput source="origem" multiline fullWidth validate={required()} helperText="Por que mudou e de onde veio o valor." />
      </SimpleForm>
    </Create>
  );
}

/* ----------------------------- Parâmetros fiscais ----------------------------- */

const nomesParametroFiscal = [
  { id: "reducao_informatica_v1", name: "Redução de informática (regra v1)" },
  { id: "interna_informatica", name: "Alíquota interna de informática (regras v2 e v3)" },
  { id: "fecoep", name: "FECOEP (regra v3)" },
];

export function ParametroFiscalLista() {
  return (
    <List title="Parâmetros fiscais" sort={{ field: "vigente_desde", order: "DESC" }} actions={<AcoesSoAdmin />} empty={false}>
      {avisoHistorico}
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <TextField source="chave" />
        <TextField source="uf" />
        <CampoPercentual source="valor" />
        <CampoData source="vigente_desde" />
        <TextField source="fonte" />
      </Datagrid>
    </List>
  );
}

export function ParametroFiscalCriar() {
  return (
    <Create title="Nova vigência de parâmetro fiscal" redirect="list">
      <SimpleForm>
        {avisoHistorico}
        <SelectInput source="chave" choices={nomesParametroFiscal} validate={required()} />
        <TextInput source="uf" defaultValue="SE" validate={required()} inputProps={{ maxLength: 2 }} />
        <EntradaPercentual source="valor" validate={required()} />
        <DateInput source="vigente_desde" validate={required()} />
        <TextInput
          source="fonte"
          multiline
          fullWidth
          validate={required()}
          helperText="Artigo e documento exatos (ex.: RICMS-SE, art. 40, IX)."
        />
      </SimpleForm>
    </Create>
  );
}

/* ----------------------------- Regra de preço ----------------------------- */

const regras = [
  { id: "v1-legado", name: "v1-legado (reproduz a planilha)" },
  { id: "v2", name: "v2 (correções do ICMS)" },
  { id: "v3", name: "v3 (fórmula da SEFAZ-SE, FECOEP à parte — D107)" },
];

export function RegraPrecoLista() {
  return (
    <List title="Regra de preço" sort={{ field: "vigente_desde", order: "DESC" }} actions={<AcoesSoAdmin />} empty={false}>
      {avisoHistorico}
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <TextField source="regra" />
        <CampoData source="vigente_desde" />
        <TextField source="justificativa" />
      </Datagrid>
    </List>
  );
}

export function RegraPrecoCriar() {
  return (
    <Create title="Trocar a regra de preço" redirect="list">
      <SimpleForm>
        <Alert severity="warning" sx={{ mb: 2 }}>
          A regra v2 muda o cálculo do ICMS (docs/contexto/13 e 14). Só ative com aprovação do Thiago e do contador.
          Preços já calculados continuam com a regra da época.
        </Alert>
        <SelectInput source="regra" choices={regras} validate={required()} />
        <DateInput source="vigente_desde" validate={required()} />
        <TextInput source="justificativa" multiline fullWidth validate={required()} />
      </SimpleForm>
    </Create>
  );
}

/* ---------------------------- Parâmetros do sistema ---------------------------- */

const ROTULOS_PARAMETRO_SISTEMA = [{ id: "cotacao_prazo_dias", name: "Prazo de validade da cotação (dias)" }];

export function ParametroSistemaLista() {
  return (
    <>
    <List
      title="Parâmetros do sistema"
      sort={{ field: "vigente_desde", order: "DESC" }}
      actions={<AcoesSoAdmin />}
      empty={false}
    >
      {avisoHistorico}
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <SelectField source="chave" choices={ROTULOS_PARAMETRO_SISTEMA} />
        <NumberField source="valor" />
        <CampoData source="vigente_desde" />
        <TextField source="origem" />
      </Datagrid>
    </List>
    <PerfisPreco />
    <ModeloPropostaParametro />
    <RegrasAlertaParametro />
    <OneDriveParametro />
    <GruposESetores />
    <TiposDocumentoParametro />
    </>
  );
}

export function ParametroSistemaCriar() {
  return (
    <Create title="Nova vigência de parâmetro do sistema" redirect="list">
      <SimpleForm defaultValues={{ chave: "cotacao_prazo_dias" }}>
        {avisoHistorico}
        <SelectInput source="chave" choices={ROTULOS_PARAMETRO_SISTEMA} validate={required()} />
        <NumberInput source="valor" min={1} step={1} validate={required()} />
        <DateInput source="vigente_desde" validate={required()} />
        <TextInput source="origem" multiline fullWidth validate={required()} helperText="Motivo da alteração." />
      </SimpleForm>
    </Create>
  );
}
